import React, { ReactElement } from 'react';
import { useHistory } from 'react-router';
import * as RegisterSelectors from '../../store/selectors';
import {
  AppGroupText,
  AppMultipleSelect,
  AppSelect,
  AppDatePicker,
  AppTextField,
  AppRadioGroup,
  AppCommon,
} from 'components/molecules/FormControls';
import { Control, useFieldArray, useForm } from 'react-hook-form';
import _ from 'lodash';
import { StateStatus } from 'redux/utils/common';
import RegisterActions from '../../store/actions';
import Button, { ButtonProps } from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import useStyles from './styles';
import { TranslatedTextV2 } from 'components/atoms/TranslatedText';
import EmailOutlinedIcon from '@material-ui/icons/EmailOutlined';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import { useDispatch } from 'react-redux';
import { Gender } from 'config';
import { TranlsationKey } from '../../configuration';
import AppButton from 'components/atoms/Button';
import { NoNullFields } from 'utils/generics';
import MobilePhonesField, {
  MobilePhoneDefault,
  MobilePhonesFieldType,
} from 'components/app_common/MobilePhonesField';
import { showToastAction } from 'components/atoms/ToastMessage';

interface IFormData {
  firstname: string | null;
  lastname: string | null;
  email: string | null;
  password: string | null;
  confirmPassword: string | null;
  mobileNumber: MobilePhonesFieldType;
}

// add types or interface
const defaultValues: IFormData = {
  firstname: null,
  lastname: null,
  email: null,
  password: null,
  confirmPassword: null,
  mobileNumber: [MobilePhoneDefault],
};

export default function RegisterForm(): ReactElement {
  console.log('Render RegisterForm:');

  const classes = useStyles();
  const {
    setValue,
    watch,
    handleSubmit,
    formState: { errors },
    control,
    getValues,
  } = useForm<IFormData>({
    defaultValues,
  });

  const dispatch = useDispatch();

  const onSubmit = (_data: NoNullFields<IFormData>) => {
    const { confirmPassword, mobileNumber, ...data } = _data;
    console.log('data', _data);

    dispatch(
      RegisterActions.registerUserAction({
        data: {
          ...data,
          mobileNumber: mobileNumber.map((el) => `${el.phoneStart}${el.phone}`),
        },
        onFinish: (success: boolean, errorMessage?: string) => {
          success
            ? showToastAction(dispatch, 'Register success', 'success')
            : showToastAction(dispatch, errorMessage, 'error');
        },
      })
    );
  };

  return (
    <form className={classes.form} onSubmit={handleSubmit(onSubmit)} noValidate>
      <AppTextField
        variant="outlined"
        margin="normal"
        error={'firstname' in errors}
        helperText={errors.firstname && errors.firstname.message}
        rules={{
          required: <RequiredField />,
        }}
        required
        fullWidth
        id="firstname"
        control={control}
        label={
          <TranslatedTextV2
            defaultText={'First name'}
            translationKey={`${TranlsationKey}.firstname`}
          />
        }
        autoComplete="firstname"
        name="firstname"
      />

      {/* <AppTextField
        variant="outlined"
        margin="normal"
        error={'lastname' in errors}
        helperText={errors.lastname && errors.lastname.message}
        rules={{
          required: <RequiredField />,
        }}
        required
        fullWidth
        id="lastname"
        label={
          <TranslatedTextV2
            defaultText={'Last name'}
            translationKey={`${TranlsationKey}.lastname`}
          />
        }
        control={control}
        autoComplete="lastname"
        name="lastname"
      /> */}

      {/* <MobilePhonesField
        errors={errors}
        control={control}
        fieldName="mobileNumber"
      /> */}

      <AppTextField
        variant="outlined"
        margin="normal"
        required
        fullWidth
        id="email"
        label={
          <TranslatedTextV2
            defaultText={'Email Address'}
            translationKey={`${TranlsationKey}.email`}
          />
        }
        name="email"
        autoComplete="email"
        control={control}
        error={'email' in errors}
        helperText={errors.email && errors.email.message}
        rules={{
          required: <RequiredField />,
          pattern: {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
            message: 'Enter a valid e-mail address',
          },
        }}
        icon={{
          // position: 'right',
          ComponentRight: <EmailOutlinedIcon />,
        }}
      />

      <AppTextField
        variant="outlined"
        margin="normal"
        required
        fullWidth
        control={control}
        error={'password' in errors}
        rules={{
          required: <RequiredField />,
          minLength: {
            value: 8,
            message: 'Password must have at least 8 characters',
          },
        }}
        label={
          <TranslatedTextV2
            defaultText={'Password'}
            translationKey={`${TranlsationKey}.password`}
          />
        }
        helperText={errors.password && errors.password.message}
        name="password"
        type="password"
        id="password"
        icon={{
          // position: 'right',
          ComponentRight: <LockOutlinedIcon />,
        }}
      />
      <AppTextField
        variant="outlined"
        margin="normal"
        required
        fullWidth
        name="confirmPassword"
        error={'confirmPassword' in errors}
        label={
          <TranslatedTextV2
            defaultText={'Confirm Password'}
            translationKey={`${TranlsationKey}.confirmPassword`}
          />
        }
        helperText={errors.confirmPassword && errors.confirmPassword.message}
        type="password"
        id="confirmPassword"
        control={control}
        rules={{
          required: <RequiredField />,
          validate: () =>
            getValues('password') === getValues('confirmPassword') ||
            'The passwords do not match',
        }}
        icon={{
          // position: 'right',
          ComponentRight: <LockOutlinedIcon />,
        }}
      />

      <Box display={'flex'} alignItems={'center'} justifyContent={'center'}>
        <SubmitWrapper
          type="submit"
          fullWidth
          variant="contained"
          color="secondary"
          className={classes.submit}
        >
          <TranslatedTextV2
            defaultText="Sign Up"
            translationKey={`${TranlsationKey}.signup`}
          />
        </SubmitWrapper>
      </Box>
    </form>
  );
}

const RequiredField = () => {
  return <AppCommon.RequiredField />;
};

interface IButtonProps extends ButtonProps {}

const SubmitWrapper = (props: IButtonProps) => {
  const registerStatus = RegisterSelectors.useGetRegisterStatus();

  return (
    <AppButton
      {...props}
      disabled={registerStatus === StateStatus.Pending}
      text={props.children}
    />
  );
};
